<script lang="ts">
  import type { Snippet } from "svelte";
  import { link } from "../../router.svelte";

  interface Props {
    variant?: "primary" | "ghost" | "danger";
    size?: "sm" | "md";
    type?: "button" | "submit";
    disabled?: boolean;
    loading?: boolean;
    href?: string;
    fullWidth?: boolean;
    ariaLabel?: string;
    title?: string;
    dataRowFocus?: boolean;
    onclick?: (event: MouseEvent) => void;
    children: Snippet;
  }

  const {
    variant = "primary",
    size = "md",
    type = "button",
    disabled = false,
    loading = false,
    href,
    fullWidth = false,
    ariaLabel,
    title,
    dataRowFocus = false,
    onclick,
    children
  }: Props = $props();

  const isDisabled = $derived(disabled || loading);
</script>

{#if href && !isDisabled}
  <a
    {href}
    use:link={{ beforeNavigate: onclick }}
    class="btn btn-{variant} size-{size}"
    class:full={fullWidth}
    aria-label={ariaLabel}
    data-row-focus={dataRowFocus ? "" : undefined}
    {title}
  >
    {@render children()}
  </a>
{:else}
  <button
    {type}
    class="btn btn-{variant} size-{size}"
    class:full={fullWidth}
    disabled={isDisabled}
    aria-busy={loading}
    aria-label={ariaLabel}
    data-row-focus={dataRowFocus ? "" : undefined}
    {title}
    {onclick}
  >
    {#if loading}<span class="spinner" aria-hidden="true"></span>{/if}
    {@render children()}
  </button>
{/if}

<style>
  /* Ported from landing .btn / .btn-primary / .btn-ghost. */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 18px;
    min-height: 40px;
    border-radius: var(--radius-btn);
    border: 1px solid transparent;
    transition: transform var(--dur-1) var(--ease), box-shadow var(--dur-3) var(--ease),
      background var(--dur-3) var(--ease), border-color var(--dur-3) var(--ease);
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
  }
  .size-sm {
    padding: 7px 12px;
    min-height: 34px;
    font-size: 13px;
  }
  .full {
    width: 100%;
  }
  .btn-primary {
    background: var(--accent);
    /* The site's ink-on-accent fill: --accent-ink names the literal it hard-codes. */
    color: var(--accent-ink);
    font-weight: 700;
    box-shadow: var(--glow-btn);
  }
  .btn-primary:hover {
    transform: translateY(-1px);
    background: var(--accent-bright);
    box-shadow: var(--glow-btn-hover);
  }
  .btn-ghost {
    background: transparent;
    color: var(--text);
    /* --line-bright carries the site's .btn-ghost border: white at 0.14 alpha. */
    border-color: var(--line-bright);
  }
  .btn-ghost:hover {
    border-color: var(--accent);
    color: var(--heading);
  }
  .btn-danger {
    background: var(--err-hover);
    color: var(--err-text);
    border-color: var(--err-border);
  }
  .btn-danger:hover {
    background: var(--err-active);
    border-color: var(--err);
  }
  /* Press settles the hover lift back onto the baseline, so the button reads as
   * pushed rather than as still hovering; the primary's glow relaxes with it. */
  .btn:active:not(:disabled) {
    transform: translateY(0);
  }
  .btn-primary:active:not(:disabled) {
    box-shadow: var(--glow-btn);
  }
  .btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: btn-spin 0.7s linear infinite;
  }
  @keyframes btn-spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
